import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Section } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  Badge,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { ArrowLeft, CreditCard } from 'lucide-react';
import {
  useInvoice,
  usePaymentMethods,
  invoiceStatusTone,
  isInvoicePaid,
  type InvoiceLineItem,
} from '@/api/billing';
import { formatCurrency } from '@/lib/format-currency';
import { formatDate } from '@/lib/format-date';
import { PaymentDialog } from './PaymentDialog';
import { brand } from '@/config/brand';
import styles from './Billing.module.css';

export function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const { data: methods } = usePaymentMethods();
  const [payOpen, setPayOpen] = useState(false);

  const cols = useMemo<ColumnDef<InvoiceLineItem, unknown>[]>(
    () => [
      {
        id: 'item',
        header: 'Item',
        cell: ({ row }) => (
          <span style={{ display: 'grid', gap: 1 }}>
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
              {row.original.service_name || row.original.invoice_item_name || '—'}
            </span>
            {row.original.invoice_item_apply_startdate && (
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                {formatDate(row.original.invoice_item_apply_startdate)} –{' '}
                {formatDate(row.original.invoice_item_apply_enddate)}
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'qty',
        header: 'Qty',
        cell: ({ row }) =>
          `${row.original.usage_quantity ?? 1}${row.original.usage_unit ? ` ${row.original.usage_unit}` : ''}`,
      },
      {
        id: 'tax',
        header: 'Tax',
        cell: ({ row }) =>
          row.original.tax_percentage != null
            ? `${(row.original.tax_percentage * 100).toFixed(0)}%`
            : '—',
      },
      {
        id: 'amount',
        header: 'Amount',
        cell: ({ row }) =>
          formatCurrency(row.original.invoice_item_amount ?? 0, row.original.currency || 'VND'),
      },
    ],
    [],
  );

  return (
    <Section
      title={isLoading ? 'Invoice' : `Invoice #${invoice?.invoice_id ?? invoiceId}`}
      description={invoice?.invoice_description}
      actions={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
          {invoice && !isInvoicePaid(invoice.invoice_status) && brand.features.payments ? (
            <Button size="sm" onClick={() => setPayOpen(true)}>
              <CreditCard size={14} /> Make payment
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => navigate('/account/billing')}>
            <ArrowLeft size={14} /> Back to billing
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <Skeleton style={{ height: 320 }} />
      ) : !invoice ? (
        <Alert tone="warning" title="Invoice not found">
          This invoice may have been removed or you don't have access to it.
        </Alert>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card>
            <CardHeader
              actions={
                <StatusPill tone={invoiceStatusTone(invoice.invoice_status)}>
                  {invoice.invoice_status}
                </StatusPill>
              }
            >
              <CardTitle>Summary</CardTitle>
              <CardDescription>
                Created {formatDate(invoice.invoice_created_at)} · Due {formatDate(invoice.invoice_duedate)}
              </CardDescription>
            </CardHeader>
            <CardBody>
              <div className={styles.totals}>
                <div className={styles.totalRow}>
                  <span>Subtotal</span>
                  <span>{formatCurrency(invoice.invoice_subtotal)}</span>
                </div>
                <div className={styles.totalRow}>
                  <span>Tax</span>
                  <span>{formatCurrency(invoice.invoice_tax)}</span>
                </div>
                <div className={`${styles.totalRow} ${styles.totalGrand}`}>
                  <span>Total</span>
                  <span>{formatCurrency(invoice.invoice_total)}</span>
                </div>
              </div>
            </CardBody>
          </Card>

          {invoice.customer && (
            <Card>
              <CardHeader>
                <CardTitle>Billed to</CardTitle>
              </CardHeader>
              <CardBody>
                <div className={styles.billedTo}>
                  <strong>{invoice.customer.customer_name}</strong>
                  {invoice.customer.customer_email && <span>{invoice.customer.customer_email}</span>}
                  {invoice.customer.customer_phone && <span>{invoice.customer.customer_phone}</span>}
                  {invoice.customer.customer_address && (
                    <span>{invoice.customer.customer_address}</span>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Line items</CardTitle>
            </CardHeader>
            <CardBody>
              <DataTable
                columns={cols}
                data={invoice.invoice_items ?? []}
                getRowId={(r) => String(r.invoice_item_id)}
                emptyState="No line items on this invoice."
              />
            </CardBody>
          </Card>

          {!isInvoicePaid(invoice.invoice_status) && brand.features.payments && (
            <Card>
              <CardHeader
                actions={
                  <Button size="sm" onClick={() => setPayOpen(true)}>
                    <CreditCard size={14} /> Make payment
                  </Button>
                }
              >
                <CardTitle>Payment</CardTitle>
                <CardDescription>
                  {formatCurrency(invoice.invoice_total)} due. Pay by bank transfer, MoMo, PayPal or card.
                </CardDescription>
              </CardHeader>
              <CardBody>
                {(methods?.length ?? 0) === 0 ? (
                  <EmptyState
                    icon={<CreditCard size={22} />}
                    title="No saved payment methods"
                    description={'Use “Make payment” to pay by bank transfer, MoMo, PayPal or card.'}
                  />
                ) : (
                  <div className={styles.methods}>
                    {methods?.map((m) => (
                      <div key={m.id} className={styles.method}>
                        <span>
                          {m.brand ?? 'Card'} {m.last4 ? `•••• ${m.last4}` : ''}
                          {m.isDefault && (
                            <Badge tone="primary" appearance="subtle" size="sm" style={{ marginLeft: 'var(--vn-space-2)' }}>
                              Default
                            </Badge>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          <PaymentDialog
            open={payOpen}
            onOpenChange={setPayOpen}
            invoiceId={invoice.invoice_id}
            amount={invoice.invoice_total}
            customerId={invoice.customer?.customer_id}
            createdAt={invoice.invoice_created_at}
          />
        </div>
      )}
    </Section>
  );
}
